import { createSlice } from '@reduxjs/toolkit'
import { mineGamePlacedBet, mineGameAutoBet, getMyBetsMineGame, mineGameOpenTile, mineGameCashOut, getMineGamePreviousRoundState, minGameTopBets, getMineGameLiveStats } from 'games/Mines/slices-thunks/mineGame.thunk'
import { minus, plus } from 'number-precision'
import pickBy from 'lodash/pickBy'
import { getPrecision } from 'utils/common-functions/index'
import { BET_RESULT } from 'utils/constants/index'
import { DEFAULT_MINE_GAME_AUTOBET_ROUNDS } from '../GameTheme/MineGame/mineGame.constants'
import { generateRandomResult } from '../GameTheme/GongaMineGame/game-helpers/index'
import { calculateMineGameCashoutOdds } from '../GameTheme/MineGame/game-helpers/index'

const initialState = {
  loading: false,
  currentPlacedBet: null,
  currentSelectedTiles: [],
  betLock: false,
  gameEnd: false,
  myBetsDataLoading: false,
  myBetsData: [],
  topBetsData: [],
  autoBetNoOfRounds: `${DEFAULT_MINE_GAME_AUTOBET_ROUNDS}`,
  autoBetCurrentPlacedBet: null,
  autoBetRo: 2,
  isAutoPlayStarted: false,
  autoBetSuccess: false,
  strategyInputs: {
    stopOnProfit: 0,
    stopOnLoss: 0,
    maxBetAmount: 0,
    onWinEnabled: false,
    onWinIncreasePercent: 0,
    onLossEnabled: false,
    onLossIncreasePercent: 0
  },
  strategyWatchers: {
    totalBetAmount: 0,
    autoBetAmount: 0,
    netReturn: 0
  },
  isLastWin: false,
  isLastLoss: false,
  liveStats: {
    totalProfit: 0,
    totalWagered: 0,
    totalWins: 0,
    totalLost: 0
  },
  wins: 0,
  losses: 0,
  profit: 0,
  wagered: 0,
  minesVolume: 1,
  cashOutResult: null,
  initialBetAmt: null
}

const {
  actions: {
    setAutoBetNoOfRounds,
    addCurrentSelectedTiles,
    removeCurrentSelectedTiles,
    callMineGameRoundEnd,
    stopAutoBet,
    setAutoPlayEndAfterAnimation,
    setStrategyInputs,
    setAutoBetAmount,
    setRoundResult,
    setLiveStats,
    resetLiveStats,
    setMinesVolume,
    setMinesWinsCount,
    setMinesLossCount,
    setMinesProfit,
    setMinesWagered,
    setAutoBetRound,
    resetCashOutResult,
    demoMinePlaceBet,
    demoMineAutoPlaceBet,
    demoMineAutoBetPending,
    demoMineCancelBet,
    demoMineGameOpenTile,
    demoMineGameCashOut
  },
  reducer
} = createSlice({
  name: 'mineGame',
  initialState,
  reducers: {
    setLiveStats: (state, action) => {
      const statistics = action.payload?.statistics
      if (statistics) {
        return {
          ...state,
          liveStats: {
            ...statistics
          }
        }
      }
      return state
    },
    setAutoPlayEndAfterAnimation: (state, action) => {
      return {
        ...state,
        autoBetCurrentPlacedBet: null,
        autoBetSuccess: false,
        isLastWin: false,
        isLastLoss: false
      }
    },
    setAutoBetNoOfRounds: (state, action) => {
      return {
        ...state,
        autoBetNoOfRounds: action.payload
      }
    },
    setRoundResult: (state, action) => {
      return {
        ...state,
        isLastWin: action.payload.isLastWin,
        isLastLoss: action.payload.isLastLoss
      }
    },
    setStrategyInputs: (state, action) => {
      return {
        ...state,
        strategyInputs: action.payload
      }
    },
    callMineGameRoundEnd: (state, action) => {
      return {
        ...state,
        betLock: false,
        gameEnd: false,
        currentSelectedTiles: initialState.currentSelectedTiles,
        currentPlacedBet: initialState.currentPlacedBet,
        isLastWin: false,
        isLastLoss: false,
        cashOutResult: null
      }
    },
    setAutoBetAmount: (state, action) => {
      return {
        ...state,
        strategyWatchers: { ...state.strategyWatchers, autoBetAmount: action.payload }
      }
    },
    addCurrentSelectedTiles: (state, action) => {
      return {
        ...state,
        currentSelectedTiles: action.payload
      }
    },
    removeCurrentSelectedTiles: (state, action) => {
      return {
        ...state,
        currentSelectedTiles: initialState.currentSelectedTiles
      }
    },
    stopAutoBet: (state, action) => {
      return {
        ...state,
        loading: false,
        autoBetNoOfRounds: initialState.autoBetNoOfRounds,
        isAutoPlayStarted: initialState.isAutoPlayStarted,
        autoBetSuccess: initialState.autoBetSuccess,
        autoBetCurrentPlacedBet: initialState.autoBetCurrentPlacedBet,
        isLastLoss: false,
        isLastWin: false,
        initialBetAmt: null // setting initial bet amount null for autobet
      }
    },
    setMinesVolume: (state, action) => ({ ...state, minesVolume: action.payload }),
    setMinesWinsCount: (state, action) => {
      return {
        ...state,
        wins: state.wins + 1
      }
    },
    setMinesLossCount: (state, action) => {
      return {
        ...state,
        losses: state.losses + 1
      }
    },
    setMinesProfit: (state, action) => {
      return {
        ...state,
        profit: plus(state.profit, minus(action.payload?.winningAmount ?? 0, action.payload?.betAmount ?? 0))
      }
    },
    setMinesWagered: (state, action) => {
      return {
        ...state,
        wagered: plus(state.wagered, action.payload)
      }
    },
    setAutoBetRound: (state, action) => {
      return {
        ...state,
        autoBetRo: action.payload
      }
    },
    resetCashOutResult: (state, action) => {
      return {
        ...state,
        cashOutResult: null
      }
    },
    setMineWinsCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          wins: action.payload.reset
        }
      }
      return {
        ...state,
        wins: state.wins + 1
      }
    },
    setMineLossCount: (state, action) => {
      if (action.payload) {
        return {
          ...state,
          losses: action.payload.reset
        }
      }
      return {
        ...state,
        losses: state.losses + 1
      }
    },
    setMineProfit: (state, action) => {
      return {
        ...state,
        profit: plus(state.profit, minus(action.payload?.winningAmount ?? 0, Number(parseFloat(action.payload?.betAmount)) ?? 0))
      }
    },
    setMineWagered: (state, action) => {
      return {
        ...state,
        wagered: plus(state.wagered, action.payload)
      }
    },
    resetLiveStats: (state, action) => {
      return ({
        ...state,
        wins: 0,
        losses: 0,
        profit: 0,
        wagered: 0
      })
    },
    demoMinePlaceBet: (state, action) => {
      const demoMineResult = generateRandomResult(action.payload.mineCount)
      return {
        ...state,
        currentPlacedBet: action.payload,
        betLock: true,
        mineExist: false,
        demoMineResult
      }
    },
    demoMineAutoBetPending: (state, action) => {
      return {
        ...state,
        autoBetSuccess: false,
        isAutoPlayStarted: true,
        autoBetCurrentPlacedBet: initialState.autoBetCurrentPlacedBet,
        loading: true,
        isLastWin: false,
        isLastLoss: false
      }
    },
    demoMineAutoPlaceBet: (state, action) => {
      let isStop = false
      const {
        betAmount,
        mineCount,
        mineGameSetting,
        normalTileAudio,
        mineTileAudio,
        tiles: selectedTiles,
        currencyPrecision
      } = action.payload

      const truthyStrategyInputs = pickBy(state.strategyInputs, value => Boolean(value) === true)
      let _netReturn = state.strategyWatchers.netReturn

      const _previousBetAmount = +(betAmount ?? 0)

      let _nextBetAmount = +(betAmount ?? 0)

      const demoMineResult = generateRandomResult(mineCount)

      // CHECK if any of the selected tile is a mine tile.
      const isMine = selectedTiles.some(tile => demoMineResult.includes(tile))
      const payout = calculateMineGameCashoutOdds(mineCount, selectedTiles?.length, mineGameSetting)
      const winningAmount = getPrecision(betAmount * payout, currencyPrecision)

      const demoResponse = {
        betAmount,
        mineCount,
        mineTiles: demoMineResult,
        winningAmount: isMine ? 0 : winningAmount,
        result: isMine ? BET_RESULT.LOST : BET_RESULT.WON
      }

      if (demoResponse.result === BET_RESULT.WON) {
        _netReturn = getPrecision(plus(_netReturn, minus(winningAmount, _previousBetAmount)), currencyPrecision)
      } else if (demoResponse.result === BET_RESULT.LOST) {
        _netReturn = getPrecision(minus(_netReturn, _previousBetAmount), currencyPrecision)
      }

      if (demoResponse.result === BET_RESULT.WON && state.strategyInputs.onWinEnabled) {
        const winPercentage = +state.strategyInputs.onWinIncreasePercent ?? 0
        _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * winPercentage / 100), currencyPrecision)
      } else if (demoResponse.result === BET_RESULT.LOST && state.strategyInputs.onLossEnabled) {
        const lossPercentage = +state.strategyInputs.onLossIncreasePercent ?? 0
        _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * lossPercentage / 100), currencyPrecision)
      }

      const _strategyWatchers = {
        totalBetAmount: getPrecision(plus(state.strategyWatchers.totalBetAmount, _previousBetAmount), currencyPrecision),
        netReturn: _netReturn,
        autoBetAmount: _nextBetAmount
      }

      Object.keys(truthyStrategyInputs).forEach(strategy => {
        switch (strategy) {
          case 'stopOnProfit':
            isStop = _netReturn >= truthyStrategyInputs[strategy]
            break
          case 'stopOnLoss':
            isStop = Math.abs(_netReturn) >= truthyStrategyInputs[strategy]
            break
          case 'maxBetAmount':
            isStop = _strategyWatchers.totalBetAmount >= truthyStrategyInputs[strategy]
            break
        }
      })
      const totalProfit = getPrecision(state.liveStats.totalProfit - parseFloat(betAmount) + parseFloat(demoResponse.winningAmount), 2)
      const totalWagered = getPrecision(state.liveStats.totalWagered + parseFloat(betAmount), 2)
      let totalLost = state.liveStats.totalLost
      let totalWins = state.liveStats.totalWins

      if (demoResponse.result === BET_RESULT.WON) {
        totalWins = state.liveStats.totalWins + 1
      } else {
        totalLost = state.liveStats.totalLost + 1
      }
      const updatedLiveStates = { totalProfit, totalWagered, totalLost, totalWins }

      demoResponse.result === BET_RESULT.WON ? normalTileAudio?.play() : mineTileAudio?.play()

      if (+state.autoBetNoOfRounds - 1 === 0 || isStop) {
        return {
          ...state,
          loading: false,
          autoBetSuccess: true,
          isAutoPlayStarted: false,
          autoBetCurrentPlacedBet: demoResponse,
          strategyWatchers: initialState.strategyWatchers,
          autoBetNoOfRounds: initialState.autoBetNoOfRounds,
          currentSelectedTiles: initialState.currentSelectedTiles,
          liveStats: updatedLiveStates
        }
      }

      return {
        ...state,
        loading: false,
        autoBetSuccess: true,
        strategyWatchers: _strategyWatchers,
        autoBetCurrentPlacedBet: demoResponse,
        autoBetNoOfRounds: state.autoBetNoOfRounds - 1,
        liveStats: updatedLiveStates
      }
    },

    demoMineCancelBet: (state, _) => {
      return {
        ...state,
        currentPlacedBet: null,
        betLock: false,
        demoMineResult: null
      }
    },
    demoMineGameOpenTile: (state, action) => {
      if (action.payload.mineTile) {
        return {
          ...state,
          currentPlacedBet: action.payload,
          currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile],
          gameEnd: true,
          demoMineResult: null,
          mineExist: action.payload.mineTile
        }
      }

      return {
        ...state,
        currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile]
      }
    },
    demoMineGameCashOut: (state, action) => {
      return {
        ...state,
        currentPlacedBet: action.payload,
        gameEnd: true,
        demoMineResult: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMineGameLiveStats.fulfilled, (state, action) => {
        const statistics = action.payload?.statistics
        if (statistics) {
          return {
            ...state,
            liveStats: {
              ...statistics
            }
          }
        }
        return state
      })
      .addCase(getMineGamePreviousRoundState.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(getMineGamePreviousRoundState.fulfilled, (state, action) => {
        if (action.payload?.hasUnfinishedGame) {
          const currentSelectedTiles = action.payload?.unfinishedGameBetDetails?.openTiles
          return {
            ...state,
            loading: false,
            currentSelectedTiles,
            currentPlacedBet: action?.payload?.unfinishedGameBetDetails,
            betLock: true
          }
        }

        return {
          ...state,
          loading: false
        }
      })
      .addCase(getMineGamePreviousRoundState.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(mineGamePlacedBet.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(mineGamePlacedBet.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          currentPlacedBet: action.payload,
          betLock: true
        }
      })
      .addCase(mineGamePlacedBet.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(mineGameAutoBet.pending, (state, action) => {
        return {
          ...state,
          autoBetSuccess: false,
          isAutoPlayStarted: true,
          autoBetCurrentPlacedBet: initialState.autoBetCurrentPlacedBet,
          loading: true,
          isLastWin: false,
          isLastLoss: false
        }
      })
      .addCase(mineGameAutoBet.fulfilled, (state, { payload }) => {
        const currencyPrecision = payload?.currencyPrecision
        const truthyStrategyInputs = pickBy(state.strategyInputs, value => Boolean(value) === true)
        let isStop = false

        let _netReturn = state.strategyWatchers.netReturn

        const _previousBetAmount = +(payload?.betAmount ?? 0)

        let _nextBetAmount = +(payload?.betAmount ?? 0)

        const _winningAmount = +(payload?.winningAmount ?? 0)

        if (payload?.result === BET_RESULT.WON) {
          _netReturn = getPrecision(plus(_netReturn, minus(_winningAmount, _previousBetAmount)), currencyPrecision)
        } else if (payload?.result === BET_RESULT.LOST) {
          _netReturn = getPrecision(minus(_netReturn, _previousBetAmount), currencyPrecision)
        }

        if (payload?.result === BET_RESULT.WON && state.strategyInputs.onWinEnabled) {
          const winPercentage = +state.strategyInputs.onWinIncreasePercent ?? 0
          _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * winPercentage / 100), currencyPrecision)
        } else if (payload?.result === BET_RESULT.LOST && state.strategyInputs.onLossEnabled) {
          const lossPercentage = +state.strategyInputs.onLossIncreasePercent ?? 0
          _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * lossPercentage / 100), currencyPrecision)
        }

        // Condition to reset on loss
        if (state.strategyInputs.onLossEnabled && payload?.result === BET_RESULT.LOST) {
          _nextBetAmount = state.initialBetAmt
        }

        // Condition to reset on win
        if (state.strategyInputs.onWinEnabled && payload?.result === BET_RESULT.WON) {
          _nextBetAmount = state.initialBetAmt
        }
        const _strategyWatchers = {
          totalBetAmount: getPrecision(plus(state.strategyWatchers.totalBetAmount, _previousBetAmount), currencyPrecision),
          netReturn: _netReturn,
          autoBetAmount: _nextBetAmount
        }

        Object.keys(truthyStrategyInputs).forEach(strategy => {
          switch (strategy) {
            case 'stopOnProfit':
              isStop = _netReturn >= truthyStrategyInputs[strategy]
              break
            case 'stopOnLoss':
              isStop = Math.abs(_netReturn) >= truthyStrategyInputs[strategy]
              break
            case 'maxBetAmount':
              isStop = _strategyWatchers.totalBetAmount >= truthyStrategyInputs[strategy]
              break
          }
        })

        if ((+state.autoBetNoOfRounds - 1 === 0) || isStop) {
          return {
            ...state,
            loading: false,
            autoBetSuccess: true,
            isAutoPlayStarted: false,
            autoBetCurrentPlacedBet: payload,
            autoBetNoOfRounds: state.autoBetNoOfRounds - 1,
            strategyWatchers: initialState.strategyWatchers,
            currentSelectedTiles: initialState.currentSelectedTiles,
            initialBetAmt: null // setting initial bet amount null for autobet
          }
        }
        return {
          ...state,
          autoBetSuccess: true,
          loading: false,
          autoBetCurrentPlacedBet: payload,
          autoBetNoOfRounds: state.autoBetNoOfRounds - 1,
          strategyWatchers: _strategyWatchers,
          ...(!state.initialBetAmt && { initialBetAmt: payload?.betAmount })
        }
      })
      .addCase(mineGameAutoBet.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          autoBetSuccess: false,
          isAutoPlayStarted: false,
          autoBetNoOfRounds: initialState.autoBetNoOfRounds,
          isLastWin: false,
          isLastLoss: false,
          initialBetAmt: null // setting initial bet amount null for autobet
        }
      })
      .addCase(getMyBetsMineGame.pending, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: true
        }
      })
      .addCase(getMyBetsMineGame.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: false,
          myBetsData: action.payload.rows
        }
      })
      .addCase(getMyBetsMineGame.rejected, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: false
        }
      })
      .addCase(mineGameOpenTile.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(mineGameOpenTile.fulfilled, (state, action) => {
        if (action.payload.mineTile) {
          return {
            ...state,
            currentPlacedBet: action.payload,
            currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile],
            myBetsData: [action.payload, ...state.myBetsData],
            gameEnd: true,
            loading: false,
            cashOutResult: {
              cashoutCondition: action.payload,
              currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile]
            }
          }
        }
        return {
          ...state,
          currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile],
          loading: false
        }
      })
      .addCase(mineGameOpenTile.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          isLastWin: false
        }
      })
      .addCase(mineGameCashOut.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(mineGameCashOut.fulfilled, (state, action) => {
        return {
          ...state,
          currentPlacedBet: action.payload,
          myBetsData: [action.payload, ...state.myBetsData],
          loading: false,
          gameEnd: true,
          cashOutResult: {
            cashoutCondition: action.payload,
            currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile]
          }
        }
      })
      .addCase(mineGameCashOut.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(minGameTopBets.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(minGameTopBets.fulfilled, (state, action) => {
        return {
          ...state,
          topBetsData: action.payload?.topBets,
          loading: false
        }
      })
      .addCase(minGameTopBets.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
  }
})

export default reducer

export {
  stopAutoBet,
  setStrategyInputs,
  setAutoBetNoOfRounds,
  setAutoPlayEndAfterAnimation,
  callMineGameRoundEnd,
  addCurrentSelectedTiles,
  removeCurrentSelectedTiles,
  setAutoBetAmount,
  setRoundResult,
  setLiveStats,
  resetLiveStats,
  setMinesVolume,
  setMinesWinsCount,
  setMinesLossCount,
  setMinesProfit,
  setMinesWagered,
  setAutoBetRound,
  resetCashOutResult,
  demoMinePlaceBet,
  demoMineAutoPlaceBet,
  demoMineAutoBetPending,
  demoMineCancelBet,
  demoMineGameOpenTile,
  demoMineGameCashOut
}
